import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { STEPS } from "../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const ease = [0.22, 1, 0.36, 1];

const StepTab = ({ s, i, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`process-step-${s.key}`}
    aria-pressed={active}
    className={`relative text-left flex-1 min-w-[150px] lg:min-w-0 px-4 py-4 sm:py-5 rounded-[4px] transition-colors duration-300 ${active ? "text-white" : "text-zinc-700 hover:text-[#0B0C10] hover:bg-zinc-100"}`}
  >
    {active && <motion.span layoutId="step-pill" className="absolute inset-0 rounded-[4px] bg-[#0B0C10]" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
    <span className="relative flex items-center gap-3">
      <span className={`font-mono text-xs ${active ? "text-[#FF6B00]" : "text-zinc-500"}`}>0{i + 1}</span>
      <span className="font-display font-bold text-lg sm:text-xl">{s.title}</span>
    </span>
  </button>
);

export const Process = () => {
  const [idx, setIdx] = useState(0);
  const s = STEPS[idx];

  return (
    <section id="process" data-testid="process-section" className="relative bg-[#F5F5F7] text-[#0B0C10] py-24 sm:py-32">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-14">
          <Reveal className="lg:col-span-7">
            <Eyebrow light>How It Works</Eyebrow>
            <h2 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.95]">
              How work moves <br className="hidden sm:block" />
              through LHGS.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9 self-end">
            <p className="text-base sm:text-lg text-zinc-700 leading-relaxed">
              Five stages, every job. Click through to see what management receives at each step.
            </p>
          </Reveal>
        </div>

        <Reveal>
          <div className="relative mb-3 h-1 rounded-full bg-zinc-200 overflow-hidden" data-testid="process-progress">
            <motion.div className="absolute inset-y-0 left-0 bg-[#FF6B00]" animate={{ width: `${((idx + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.6, ease }} />
          </div>
          <div className="flex gap-1 overflow-x-auto no-scrollbar bg-white p-1.5 rounded-[6px] border border-zinc-200">
            {STEPS.map((st, i) => (
              <StepTab key={st.key} s={st} i={i} active={i === idx} onClick={() => setIdx(i)} />
            ))}
          </div>
        </Reveal>

        <motion.div layout data-testid="process-card" className="mt-6 grid lg:grid-cols-12 gap-0 bg-white rounded-[6px] border border-zinc-200 overflow-hidden">
          <div className="lg:col-span-7 p-7 sm:p-12 flex flex-col">
            <motion.div key={s.key} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }}>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#C2410C]">Stage 0{idx + 1} / 05</p>
              <h3 data-testid="process-card-title" className="mt-4 font-display font-black text-5xl sm:text-7xl tracking-[-0.04em]">{s.title}</h3>
              <p className="mt-5 font-display text-xl sm:text-2xl font-semibold text-[#0B0C10]">{s.lead}</p>
              <p className="mt-3 text-base sm:text-lg text-zinc-700 leading-relaxed max-w-xl">{s.body}</p>
              <ul className="mt-8 grid sm:grid-cols-3 gap-3">
                {s.deliverables.map((d) => (
                  <li key={d} className="flex items-center gap-2 text-sm font-medium border border-zinc-200 rounded-[4px] px-3 py-2.5">
                    <Check className="h-4 w-4 text-[#FF6B00] shrink-0" /> {d}
                  </li>
                ))}
              </ul>
            </motion.div>
            <div className="mt-auto pt-10 flex gap-3">
              <button onClick={() => setIdx((idx + 4) % 5)} data-testid="process-prev" className="btn-ghost-dark">Previous</button>
              <button onClick={() => setIdx((idx + 1) % 5)} data-testid="process-next" className="btn-primary">
                Next stage <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="lg:col-span-5 relative min-h-[280px] sm:min-h-[360px] bg-[#0B0C10] overflow-hidden">
            {STEPS.map((st, i) => (
              <motion.img
                key={st.key}
                src={st.img}
                alt={`${st.title} stage`}
                className="absolute inset-0 h-full w-full object-cover"
                animate={{ opacity: i === idx ? 1 : 0, scale: i === idx ? 1 : 1.08 }}
                transition={{ duration: 0.8, ease }}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <span className="absolute bottom-5 left-5 font-mono text-[11px] uppercase tracking-[0.2em] text-white">IMG_{String(idx + 1).padStart(4, "0")} · {s.title}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
